import React, { useState, useEffect, useRef, ChangeEvent } from "react";
import ReactDOM from "react-dom"; // ReactDOM import
import { CompanyDto } from "src/dtos/company/companyDto";
import { ItemDto } from "src/dtos/item/itemDto";
import { SysCodeDto } from "src/dtos/code/sysCodeDto";
import { OrganizationDto } from "src/dtos/organization/organizationDto";
import { WareHouseDto } from "src/dtos/warehouse/wareHouseDto";

import { searchFormString, searchInputFormString, searchFieldWidth, searchLabelStyle } from "src/components/common/common";

import { DropDownFormContent } from "../dropdown_custom/DropDownFormContent";
import { DropDownOptionItem } from "../dropdown_custom/DropDownOptionItem";
import { AnyDto } from 'src/components/common/common';
import { defaultSysCode_Modal } from "src/dtos/code/sysCodeDto";
import { WorkPlaceDto } from "src/dtos/workplace/workPlaceDto";

interface Props<T extends AnyDto> {
   label: string;
   value: string;
   optionList: T[] | undefined;
   wrapperOption?: {
      wrapperStyle?: string;
      titleStyle?: string;
      selectStyle?: string;
      listStyle?: string; // 드롭다운 리스트(ul) 스타일 (예: max-h-[200px])
   }
   editable?: boolean;
   initLabelLess?: boolean;
   onChangeValue?: (value: string) => void;
   onChangeItem?: (selectedItem: T | undefined) => void; // "전체" 선택 시 undefined 전달
   maxOptionsCount?: number; // 렌더링할 최대 옵션 갯수 제한
}

export const SearchDropDownForm = <T extends AnyDto>(props: Props<T>) => {
   const {
      label,
      value,
      optionList,
      wrapperOption={
         wrapperStyle: "w-[200px] min-w-[200px] mr-[10px]",
         titleStyle: "w-[45px] min-w-[45px] text-left",
         selectStyle: "w-[100px] min-w-[100px]",
     },
      editable = true,
      initLabelLess = false,
      onChangeValue,
      onChangeItem,
      maxOptionsCount = 15,
   } = props;

   const [isOpen, setIsOpen] = useState(false);
   const [displayLabel, setDisplayLabel] = useState(initLabelLess ? "" : "전체");
   
    // Refs
   const dropdownWrapperRef = useRef<HTMLDivElement>(null); // 드롭다운 전체 컴포넌트의 최상위 div
   const buttonRef = useRef<HTMLButtonElement>(null);      // 드롭다운을 여는 버튼
   const listRef = useRef<HTMLUListElement>(null);         // Portal로 렌더링될 ul 요소

    // Portal로 렌더링될 리스트의 위치와 크기 상태
    const [listPosition, setListPosition] = useState({ top: 0, left: 0, width: 0 });

    // DropDownFormContent에서 반환된 이름 그대로 사용 (또는 실제 반환된 이름)
   const {
      IsCompanyDtoArray,
      IsItemDtoArray,
      IsCodeDtoArray,
      IsOrganizationDtoArray,
      IsWareHouseDtoArray,
      IsWorkPlaceDtoArray,
   } = DropDownFormContent<T>({ // 제네릭 T 전달
      optionList,
      // DropDownFormContent의 props에 value는 없으므로 제거
      isOpen, setIsOpen,
      buttonRef,
      setListPosition,
   });

   const warningComment = "initLabelLess인경우 초기값을 설정해 주세요.";

   // initLabelLess 옵션에 따른 초기값 처리
   // // 실제 값이 바뀌고 라벨 값 설정
   useEffect(() => {
      let currentLabel = initLabelLess ? warningComment : "전체";

      if (value === "" || value === defaultSysCode_Modal.minorCd) {
         currentLabel = initLabelLess ? warningComment : "전체";
      } else if (optionList) {
         let found = false;
         if (IsCompanyDtoArray(optionList)) {
            const pOptionList = optionList as CompanyDto[];
            const selectedOption = pOptionList.find(opt => opt.compCd === value);
            if (selectedOption) { currentLabel = selectedOption.compNm; found = true; }
         }
         if (!found && IsItemDtoArray(optionList)) {
            const pOptionList = optionList as ItemDto[];
            const selectedOption = pOptionList.find(opt => opt.itemCd === value);
            if (selectedOption) { currentLabel = selectedOption.itemNm; found = true; }
         }
         if (!found && IsCodeDtoArray(optionList)) {
            const pOptionList = optionList as SysCodeDto[];
            const selectedOption = pOptionList.find(opt => opt.minorCd === value);
            if (selectedOption) { currentLabel = selectedOption.codeNm.trim(); found = true; }
         }
         if (!found && IsOrganizationDtoArray(optionList)) {
            const pOptionList = optionList as OrganizationDto[];
            const selectedOption = pOptionList.find(opt => opt.orgCd === value);
            if (selectedOption) { currentLabel = selectedOption.orgNm; found = true; }
         }
         if (!found && IsWareHouseDtoArray(optionList)) {
            const pOptionList = optionList as WareHouseDto[];
            const selectedOption = pOptionList.find(opt => opt.warehouseCd === value);
            if (selectedOption) { currentLabel = selectedOption.warehouseNm; found = true; }
         }
         if (!found && IsWorkPlaceDtoArray(optionList)) {
            const pOptionList = optionList as WorkPlaceDto[];
            const selectedOption = pOptionList.find(opt => opt.workplaceCd === value);
            if (selectedOption) { currentLabel = selectedOption.workplaceNm; found = true; }
         }
      }
      setDisplayLabel(currentLabel);
   }, [value, optionList]);

   // //실제 값을 설정하는 부분
   const handleOptionSelect = (
      itemValue: string, 
      itemType: 'company' | 'item' | 'code' | 'organization' | 'warehouse' | 'workplace' | ''
   ) => {
        // console.log(`[SearchDropDownForm] handleOptionSelect called with: itemValue=${itemValue}, itemType=${itemType}`);
      onChangeValue?.(itemType === '' ? defaultSysCode_Modal.minorCd : itemValue);
      if (onChangeItem) {   
         if (itemType === '') {
            onChangeItem(defaultSysCode_Modal as T); 
         } else if (optionList) {
            let selectedFullItem: T | undefined;
            if (itemType === 'company' && IsCompanyDtoArray(optionList)) {
               const pOptionList = optionList as CompanyDto[];
               selectedFullItem = pOptionList.find(opt => opt.compCd === itemValue) as T;
            } else if (itemType === 'item' && IsItemDtoArray(optionList)) {
               const pOptionList = optionList as ItemDto[];
               selectedFullItem = pOptionList.find(opt => opt.itemCd === itemValue) as T;
            } else if (itemType === 'code' && IsCodeDtoArray(optionList)) {
               const pOptionList = optionList as SysCodeDto[];
               selectedFullItem = pOptionList.find(opt => opt.minorCd === itemValue) as T;
            } else if (itemType === 'organization' && IsOrganizationDtoArray(optionList)) {
               const pOptionList = optionList as OrganizationDto[];
               selectedFullItem = pOptionList.find(opt => opt.orgCd === itemValue) as T;
            } else if (itemType === 'warehouse' && IsWareHouseDtoArray(optionList)) {
               const pOptionList = optionList as WareHouseDto[];
               selectedFullItem = pOptionList.find(opt => opt.warehouseCd === itemValue) as T;
            } else if (itemType === 'workplace' && IsWorkPlaceDtoArray(optionList)) {
               const pOptionList = optionList as WorkPlaceDto[];
               selectedFullItem = pOptionList.find(opt => opt.workplaceCd === itemValue) as T;
            }
            if (selectedFullItem) {
               onChangeItem(selectedFullItem);
            }
         }
      }
      setIsOpen(false);
   };

    // 외부 클릭 감지하여 드롭다운 닫기
   useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
         if (
                buttonRef.current && !buttonRef.current.contains(event.target as Node) &&
            listRef.current && !listRef.current.contains(event.target as Node)
         ) {
            setIsOpen(false);
         }
      };
      if (isOpen) {
            document.addEventListener("mousedown", handleClickOutside);
        } else {
            document.removeEventListener("mousedown", handleClickOutside);
        }
      return () => {
         document.removeEventListener("mousedown", handleClickOutside);
      };
   }, [isOpen]); // buttonRef와 listRef는 ref 객체이므로 의존성 배열에서 제외해도 됨 (current는 변경돼도 리렌더링 유발 안 함)

   const DropdownListContent = (
        <ul
            ref={listRef}
            className={`fixed bg-white border border-gray-300 rounded-md shadow-lg z-50 overflow-y-auto overflow-x-hidden py-1 ${wrapperOption?.listStyle || ''}`}
            style={{ 
                top: `${listPosition.top}px`, 
                left: `${listPosition.left}px`,
                width: `${listPosition.width}px`,
                maxHeight: maxOptionsCount ? `${maxOptionsCount * 32 + 8}px` : '700px',
                marginTop: '0px'
            }}
            role="listbox"
         onClick={(e) => e.stopPropagation()} // 이벤트 전파 중단 로직 추가
        >
         {/* "전체" 옵션 */}
         {!initLabelLess && (
            <DropDownOptionItem
               itemKey="all-option"
               label="전체"
               isSelected={value === defaultSysCode_Modal.minorCd || value === ""}
               onSelect={() => handleOptionSelect(defaultSysCode_Modal.minorCd, "")}
            />
         )}
            {/* Company DTO 옵션들 */}
            {IsCompanyDtoArray(optionList) && optionList.map((option) => {
            const pOption = option as CompanyDto;
                return (
                    <DropDownOptionItem
                        key={pOption.compCd}
                        itemKey={pOption.compCd}
                        label={pOption.compNm}
                        isSelected={value === pOption.compCd}
                        onSelect={() => handleOptionSelect(pOption.compCd, "company")}
                    />
                );
            })}
            {/* Item DTO 옵션들 */}
            {IsItemDtoArray(optionList) && optionList.map((option) => {
            const pOption = option as ItemDto;
                return (
                    <DropDownOptionItem
                        key={pOption.itemCd}
                        itemKey={pOption.itemCd}
                        label={pOption.itemNm}
                        isSelected={value === pOption.itemCd}
                        onSelect={() => handleOptionSelect(pOption.itemCd, "item")}
                    />
                );
            })}
            {/* Code DTO 옵션들 */}
            {IsCodeDtoArray(optionList) && optionList.map((option) => {
            const pOption = option as SysCodeDto;
                return (
                    <DropDownOptionItem
                        key={pOption.majorCd + '_' + pOption.minorCd}
                        itemKey={pOption.majorCd + '_' + pOption.minorCd}
                        label={pOption.codeNm.trim()}
                        isSelected={value === pOption.minorCd}
                        onSelect={() => handleOptionSelect(pOption.minorCd, "code")}
                    />
                );
            })}
         {/* Organization DTO 옵션들 */}
         {IsOrganizationDtoArray(optionList) && optionList.map((option) => {
            const pOption = option as OrganizationDto;
            return (
               <DropDownOptionItem
                  key={pOption.orgCd}
                  itemKey={pOption.orgCd}
                  label={pOption.orgNm}
                  isSelected={value === pOption.orgCd}
                  onSelect={() => handleOptionSelect(pOption.orgCd, "organization")}
               />
            );
         })}
         {/* Warehouse DTO 옵션들 */}
         {IsWareHouseDtoArray(optionList) && optionList.map((option) => {
            const pOption = option as WareHouseDto;
            return (
               <DropDownOptionItem
                  key={pOption.warehouseCd}
                  itemKey={pOption.warehouseCd}
                  label={pOption.warehouseNm}
                  isSelected={value === pOption.warehouseCd}
                  onSelect={() => handleOptionSelect(pOption.warehouseCd, "warehouse")}
               />
            );
         })}
         {IsWorkPlaceDtoArray(optionList) && optionList.map((option) => {
            const pOption = option as WorkPlaceDto;
            return (
               <DropDownOptionItem
                  key={pOption.workplaceCd}
                  itemKey={pOption.workplaceCd}
                  label={pOption.workplaceNm}
                  isSelected={value === pOption.workplaceCd}
                  onSelect={() => handleOptionSelect(pOption.workplaceCd, "workplace")}
               />
            );
         })}
        </ul>
    );

   return (
      <>
         <div
            ref={dropdownWrapperRef}
            className={`${ searchFormString } ${ wrapperOption?.wrapperStyle || '' }  cursor-pointer`}
            onClick={() => {
               if (editable) {
                  setIsOpen(!isOpen);
               }
            }}
            >
            <p className={`h-[24px] flex items-center truncate shrink-0 ${wrapperOption?.titleStyle || ''}`}
            >
               {label}
            </p>
            
            {/* 실제 드롭다운 컴포넌트 */}
            <div className="relative flex-grow">
               <button
                        ref={buttonRef}
                  type="button"
                  className={`h-[50%] min-h-[24px] rounded-[6px] 
                     ${editable ? 'bg-white' : 'bg-gray-100'}
                     border border-gray-300 flex items-center justify-between text-left px-2 cursor-pointer ${wrapperOption?.selectStyle || ''}`}
                  aria-haspopup="listbox"
                  aria-expanded={isOpen}
               >
                  <span className="truncate">{displayLabel}</span>
                  <svg className={`w-4 h-4 text-gray-700 shrink-0 transform transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd"></path></svg>
               </button>
               {isOpen && ReactDOM.createPortal(DropdownListContent, document.body)}
            </div>
         </div>
      </>
   );
};